import { Component, EventEmitter, OnInit, Output, inject, Inject } from '@angular/core';
import { TreeModule } from '@ali-hm/angular-tree-component';
import projectCategoriesSource from '../admin/projects/projectcategories';
import exampleProjectsSource from '../admin/projects/projectexamples';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-insert-object-dialog',
  standalone: true,
  imports: [
    TreeModule,
    CommonModule,
    MatProgressSpinnerModule,
    MatButtonModule,
    MatIconModule,
    FormsModule
  ],
  templateUrl: './insert-object-dialog.component.html',
  styleUrl: './insert-object-dialog.component.scss'
})
export class InsertObjectDialogComponent implements OnInit {
  private afs = inject(AngularFirestore);

  nodes = projectCategoriesSource.nodes.root;
  examples = exampleProjectsSource.examples.root;

  displayedProjects: any[] = [];
  selectedProjectDisplay: any = null;
  isLoadingProjects: boolean = false;
  currentCategoryId: string | null = null;
  currentView: 'category' | 'own' | 'search' | 'none' = 'none';

  // Search related properties
  searchText: string = '';
  allPublicProjectsForSearch: any[] = [];
  allOwnProjectsForSearch: any[] = [];
  isOwnProjectsFullyLoadedForSearch: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<InsertObjectDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { ownProjects: any[] }
  ) { }

  ngOnInit(): void {
    this.loadAllPublicProjectsForSearch();
    // Use own projects data passed from parent instead of loading from Firebase
    if (this.data?.ownProjects) {
      this.allOwnProjectsForSearch = this.data.ownProjects;
      this.isOwnProjectsFullyLoadedForSearch = true;
      console.log('Own projects received from parent:', this.allOwnProjectsForSearch.length);

      // Show own projects by default
      this.loadOwnProjects();
    } else {
      this.allOwnProjectsForSearch = [];
      this.isOwnProjectsFullyLoadedForSearch = true;
      console.log('No own projects data provided from parent');
    }
  }

  loadAllPublicProjectsForSearch(): void {
    this.afs.collection('projectspublic').valueChanges({ idField: 'id' })
      .pipe(catchError(err => {
        console.error('Error fetching all public projects for search:', err);
        return of([]); // Return empty array on error
      }))
      .subscribe(projects => {
        this.allPublicProjectsForSearch = projects;
        console.log('All public projects loaded for search:', this.allPublicProjectsForSearch.length);
      });
  }

  onClose() {
    this.dialogRef.close();
  }

  onNodeActivate(event: any): void {
    this.searchText = ''; // Clear search on tree navigation
    this.selectedProjectDisplay = null;
    const nodeData = event.node.data;
    const categoryId = nodeData?.id;

    if (this.currentView === 'category' && this.currentCategoryId === categoryId) {
      return;
    }

    this.currentCategoryId = categoryId;
    this.currentView = 'category';

    if (categoryId) {
      this.loadProjectsForCategory(categoryId);
    } else {
      this.displayedProjects = [];
      this.currentCategoryId = null;
      this.currentView = 'none';
    }
  }

  loadProjectsForCategory(categoryId: string): void {
    this.isLoadingProjects = true;
    this.displayedProjects = [];
    this.afs.collection('projectspublic', ref => ref.where('publicCategoryID', '==', categoryId))
      .valueChanges({ idField: 'id' })
      .subscribe({
        next: (projects) => {
          this.displayedProjects = projects;
          this.isLoadingProjects = false;
        },
        error: (err) => {
          console.error('Error fetching projects for category:', err);
          this.displayedProjects = [];
          this.isLoadingProjects = false;
        }
      });
  }

  loadOwnProjects(): void {
    this.searchText = ''; // Clear search when explicitly loading own projects for display
    this.selectedProjectDisplay = null;
    this.currentCategoryId = null;
    this.currentView = 'own';
    this.isLoadingProjects = false; // No loading needed since data is already available

    // Use cached data from parent
    this.displayedProjects = this.allOwnProjectsForSearch;
    console.log('Displaying own projects from cache:', this.displayedProjects.length);
  }

  onProjectSelect(project: any): void {
    this.selectedProjectDisplay = project;
    console.log('Project selected in UI:', project);
  }

  addSelectedProjectToScene(): void {
    if (this.selectedProjectDisplay) {
      console.log('Adding to scene via button:', this.selectedProjectDisplay);
      this.dialogRef.close(this.selectedProjectDisplay);
    } else {
      console.log('No project selected to add to scene.');
    }
  }

  // New Search Methods
  onSearchTextChange(): void {
    if (!this.searchText || this.searchText.trim() === '') {
      this.clearSearchAndResetView();
      return;
    }
    this.currentView = 'search';
    this.selectedProjectDisplay = null; // Clear selection when search text changes
    this.executeSearch();
  }

  clearSearchAndResetView(): void {
    this.searchText = '';
    this.displayedProjects = [];
    this.currentView = 'none';
    this.isLoadingProjects = false;
    this.selectedProjectDisplay = null;
    // Optionally, could try to restore previous category view if we stored that state
  }

  executeSearch(): void {
    this.isLoadingProjects = true;
    this.displayedProjects = [];

    // Both allPublicProjectsForSearch and allOwnProjectsForSearch should be ready
    const combinedMap = new Map<string, any>();
    this.allOwnProjectsForSearch.forEach(p => combinedMap.set(p.id, p));
    this.allPublicProjectsForSearch.forEach(p => combinedMap.set(p.id, p)); // Public projects can overwrite own if IDs clash - adjust if needed

    const searchableProjects = Array.from(combinedMap.values());
    const searchTerm = this.searchText.toLowerCase().trim();

    if (searchTerm === '') { // Double check after operations if searchText was cleared
      this.clearSearchAndResetView();
      return;
    }

    this.displayedProjects = searchableProjects.filter(project =>
      project.name && project.name.toLowerCase().includes(searchTerm)
    );
    this.isLoadingProjects = false;
  }
}
